import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const PublicHeader = ({ isHomePage = false, transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up');
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // For homepage, only show white background after scrolling past hero section
      if (isHomePage) {
        setIsScrolled(currentScrollY > 600);
        
        // Track scroll direction for mobile behavior
        if (currentScrollY > lastScrollY) {
          setScrollDirection('down');
        } else {
          setScrollDirection('up');
        }
        setLastScrollY(currentScrollY);
      } else {
        setIsScrolled(currentScrollY > 10);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage, lastScrollY]);

  const navLinks = [
    { path: '/pricing', label: 'Pricing' },
    { path: '/blog', label: 'Resources' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Determine if header should be visible on mobile
  const isMobileVisible = !isHomePage || !transparent || isScrolled || (scrollDirection === 'up' && lastScrollY > 600);

  const headerClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    transparent && !isScrolled && !isMobileMenuOpen
      ? 'bg-transparent'
      : 'bg-white shadow-sm'
  } ${
    isHomePage && !isMobileVisible ? 'md:block hidden' : ''
  }`;

  // Hide content when transparent on homepage
  const shouldShowContent = !isHomePage || !transparent || isScrolled || isMobileMenuOpen;

  const textColorClass = transparent && !isScrolled && !isMobileMenuOpen
    ? 'text-white'
    : 'text-gray-900';

  const logoTextColorClass = transparent && !isScrolled && !isMobileMenuOpen
    ? 'text-white'
    : 'text-blue-600';

  return (
    <>
      <header className={headerClasses}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className={`flex items-center ${shouldShowContent ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              <img 
                src="/images/categorix-logo.png" 
                alt="CategoriX" 
                className="h-8 sm:h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className={`hidden md:flex items-center space-x-8 ${shouldShowContent ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors ${
                    isActive(link.path)
                      ? transparent && !isScrolled
                        ? 'text-white'
                        : 'text-blue-600'
                      : `${textColorClass} hover:text-blue-600`
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* CTA Buttons */}
              <div className="flex items-center space-x-4 ml-8">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className={`font-medium transition-colors ${
                        transparent && !isScrolled
                          ? 'text-white hover:text-blue-200'
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${textColorClass} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${shouldShowContent ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
            >
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive(link.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="border-t border-gray-200 pt-4 mt-4">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      className="block px-3 py-2 mt-1 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700 text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* Spacer for fixed header - only on non-transparent headers */}
      {!transparent && <div className="h-16" />}
    </>
  );
};

export default PublicHeader; 